import { HttpErrorResponse } from '@angular/common/http';
import { AccountInfo, IdTokenClaims } from '@azure/msal-common';
import { EventMessage, EventType } from '@azure/msal-browser';
import { apm, init as initApm } from '@elastic/apm-rum';

export type ElasticApmConfig = {
  enabled?: boolean;
  serviceName?: string;
  serverUrl?: string;
  environment?: string;
  serviceVersion?: string;
  distributedTracingOrigins?: string[];
  captureBrowserNetworkFailures?: boolean;
  captureAssetLoadFailures?: boolean;
  orderTrackingSlowLoadThresholdMs?: number;
  blockedResourceHosts?: string[];
  disableInstrumentations?: string[];
  ignoreTransactions?: Array<string | RegExp>;
};

export type ObservabilityEnvironment = {
  envName?: string;
  appVersion?: string;
  apiUrl?: string;
  enableAnalytics?: boolean;
  elasticApm?: ElasticApmConfig;
};

export type ServerHttpErrorDiagnostics = {
  requestMethod?: string;
  requestUrl?: string;
  feature?: string;
  correlationId?: string;
  statusText?: string;
  failureReason?: string;
};

export type BrowserNetworkFailureDiagnostics = ServerHttpErrorDiagnostics;

export type RouteNavigationDiagnostics = {
  routeTitle?: string;
  routePath?: string;
};

export type MsalRedirectFailureDiagnostics = {
  route?: string;
  errorCode?: string;
  errorDescription?: string;
  rawErrorMessage?: string;
};

type IdTokenClaimsWithApmUser = IdTokenClaims & {
  oid?: string;
  sub?: string;
};

type MsalErrorLike = {
  errorCode?: string;
  errorMessage?: string;
  message?: string;
  correlationId?: string;
};

const GLOBAL_ERROR_HANDLERS_REGISTRATION_KEY = '__myLinkGlobalErrorHandlersRegistered';
const DEFAULT_BLOCKED_RESOURCE_HOSTS = [
  'px.ads.linkedin.com:443',
  'www.google-analytics.com:443',
  'www.google.com:443',
];
const MSAL_FAILURE_EVENT_TYPES: EventType[] = [
  EventType.LOGIN_FAILURE,
  EventType.ACQUIRE_TOKEN_FAILURE,
  EventType.SSO_SILENT_FAILURE,
  EventType.LOGOUT_FAILURE,
];
const EXPLICIT_CLIENT_ERROR_FAILURE_TYPES = new Set([
  'msal_auth',
  'network_error',
  'client_error',
  'asset_load',
  'registration_validate',
  'profile_load',
]);

const capturedServerHttpErrors = new WeakSet<HttpErrorResponse>();
const capturedBrowserNetworkFailures = new WeakSet<HttpErrorResponse>();

const sanitizeString = (value?: string | null): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const sanitizeUrl = (value?: string | null): string | undefined => {
  const url = sanitizeString(value);
  if (!url) return undefined;

  const locationOrigin = typeof window !== 'undefined' ? window.location.origin : undefined;
  try {
    const parsed = new URL(url, locationOrigin);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return url.split('?')[0].split('#')[0];
  }
};

const getPathname = (url?: string): string | undefined => {
  const sanitizedUrl = sanitizeString(url);
  if (!sanitizedUrl) return undefined;

  const locationOrigin = typeof window !== 'undefined' ? window.location.origin : undefined;
  try {
    return new URL(sanitizedUrl, locationOrigin).pathname;
  } catch {
    return undefined;
  }
};

const getCurrentRoute = (): string => {
  if (typeof window === 'undefined') return '';
  return window.location.pathname || '';
};

const getApiOrigin = (url?: string): string | undefined => {
  const sanitizedUrl = sanitizeString(url);
  if (!sanitizedUrl) return undefined;

  const locationOrigin = typeof window !== 'undefined' ? window.location.origin : undefined;
  try {
    return new URL(sanitizedUrl, locationOrigin).origin;
  } catch {
    return undefined;
  }
};

export const getDistributedTracingOrigins = (environment: ObservabilityEnvironment): string[] => {
  const configuredOrigins = Array.isArray(environment.elasticApm?.distributedTracingOrigins)
    ? environment.elasticApm?.distributedTracingOrigins
    : [];

  const normalizedConfiguredOrigins = configuredOrigins
    .map((origin) => getApiOrigin(origin) || sanitizeString(origin))
    .filter((origin): origin is string => Boolean(origin));

  const discoveredOrigin = getApiOrigin(environment.apiUrl);
  const result = discoveredOrigin ? [...normalizedConfiguredOrigins, discoveredOrigin] : normalizedConfiguredOrigins;

  return Array.from(new Set(result));
};

const normalizeBlockedResourceHostCandidates = (hostOrUrl?: string): string[] => {
  const sanitized = sanitizeString(hostOrUrl)?.toLowerCase();
  if (!sanitized) return [];

  const candidates = new Set<string>();
  const withoutProtocol = sanitized.replace(/^[a-z]+:\/\//, '');
  const hostPort = withoutProtocol.split('/')[0];
  const hostParts = hostPort.split(':');
  const host = sanitizeString(hostParts[0]);
  const port = sanitizeString(hostParts[1]);

  if (!host) return [];

  candidates.add(host);
  if (port && /^[0-9]+$/.test(port)) {
    candidates.add(`${host}:${port}`);
  } else {
    candidates.add(`${host}:443`);
  }

  return Array.from(candidates);
};

const normalizeBlockedResourceHosts = (environment: ObservabilityEnvironment): Set<string> => {
  const configuredHosts = Array.isArray(environment.elasticApm?.blockedResourceHosts)
    ? environment.elasticApm?.blockedResourceHosts
    : [];

  const allHosts = [...DEFAULT_BLOCKED_RESOURCE_HOSTS, ...configuredHosts];
  const normalized = allHosts.reduce<string[]>((acc, host) => {
    acc.push(...normalizeBlockedResourceHostCandidates(host));
    return acc;
  }, []);

  return new Set(normalized);
};

const extractResourceHostsFromText = (text?: string): string[] => {
  const sanitizedText = sanitizeString(text);
  if (!sanitizedText) return [];

  const matches = sanitizedText.match(/[a-z0-9.-]+\.[a-z]{2,}(?::\d+)?/gi) || [];
  return matches.map((match) => match.toLowerCase()).filter((match) => !match.includes(' '));
};

const getResourceHostFromUrl = (url?: string): string | undefined => {
  const sanitizedUrl = sanitizeString(url);
  if (!sanitizedUrl) return undefined;

  const locationOrigin = typeof window !== 'undefined' ? window.location.origin : undefined;
  try {
    const parsed = new URL(sanitizedUrl, locationOrigin);
    const fallbackPort = parsed.protocol === 'https:' ? '443' : parsed.protocol === 'http:' ? '80' : '';
    const port = parsed.port || fallbackPort;
    return port ? `${parsed.hostname.toLowerCase()}:${port}` : parsed.hostname.toLowerCase();
  } catch {
    return undefined;
  }
};

const isBlockedHost = (blockedHosts: Set<string>, candidate?: string): boolean => {
  const normalized = sanitizeString(candidate)?.toLowerCase();
  if (!normalized) return false;

  const hostCandidates = normalizeBlockedResourceHostCandidates(normalized);
  return hostCandidates.some((item) => blockedHosts.has(item));
};

const getFailureType = (entry: any): string | undefined => {
  return sanitizeString(
    entry?.context?.tags?.failure_type ||
      entry?.context?.custom?.failure_type ||
      entry?.labels?.failure_type ||
      entry?.tags?.failure_type,
  );
};

const extractHttpStatus = (entry: any): number | undefined => {
  const candidate =
    entry?.context?.custom?.status ??
    entry?.context?.response?.status_code ??
    entry?.context?.http?.status_code ??
    entry?.exception?.attributes?.status;

  const status = Number(candidate);
  if (!Number.isFinite(status)) return undefined;
  return status;
};

const shouldKeepError = (entry: any): boolean => {
  const status = extractHttpStatus(entry);
  if (typeof status === 'number' && status < 500) {
    const failureType = getFailureType(entry);
    return Boolean(failureType && EXPLICIT_CLIENT_ERROR_FAILURE_TYPES.has(failureType));
  }

  return true;
};

const shouldDropBlockedHostError = (entry: any, blockedHosts: Set<string>): boolean => {
  if (blockedHosts.size === 0) return false;

  const urls = [
    entry?.context?.request?.url,
    entry?.context?.request?.url?.full,
    entry?.context?.http?.url,
    entry?.context?.page?.url,
    entry?.context?.custom?.url,
  ];

  const hasBlockedUrl = urls
    .map((url: string | undefined) => getResourceHostFromUrl(url))
    .filter((host): host is string => Boolean(host))
    .some((host) => isBlockedHost(blockedHosts, host));

  if (hasBlockedUrl) return true;

  const messages = [entry?.log?.message, entry?.exception?.message, entry?.exception?.attributes?.message]
    .map((value: string | undefined) => sanitizeString(value))
    .filter((value): value is string => Boolean(value));

  const extractedHosts = messages.reduce<string[]>((hosts, message) => {
    hosts.push(...extractResourceHostsFromText(message));
    return hosts;
  }, []);

  return extractedHosts.some((host) => isBlockedHost(blockedHosts, host));
};

const filterBlockedResourceTransactions = (payload: any, blockedHosts: Set<string>): any => {
  if (!Array.isArray(payload?.transactions) || blockedHosts.size === 0) return payload;

  payload.transactions = payload.transactions.filter((transaction: any) => {
    const transactionUrl =
      transaction?.context?.request?.url ||
      transaction?.context?.request?.url?.full ||
      transaction?.context?.page?.url ||
      transaction?.context?.http?.url;

    const transactionHost = getResourceHostFromUrl(transactionUrl);
    if (transactionHost && isBlockedHost(blockedHosts, transactionHost)) {
      return false;
    }

    const transactionNameHosts = extractResourceHostsFromText(transaction?.name || '');
    if (transactionNameHosts.some((host) => isBlockedHost(blockedHosts, host))) {
      return false;
    }

    if (Array.isArray(transaction?.spans)) {
      transaction.spans = transaction.spans.filter((span: any) => {
        const spanUrl = span?.context?.http?.url || span?.context?.destination?.service?.resource;
        const spanHost =
          getResourceHostFromUrl(spanUrl) || sanitizeString(span?.context?.destination?.service?.resource);
        return !isBlockedHost(blockedHosts, spanHost);
      });
    }

    return true;
  });

  return payload;
};

const shouldCaptureBrowserNetworkFailures = (environment: ObservabilityEnvironment): boolean => {
  return environment.elasticApm?.captureBrowserNetworkFailures !== false;
};

const shouldCaptureAssetLoadFailures = (environment: ObservabilityEnvironment): boolean => {
  return environment.elasticApm?.captureAssetLoadFailures !== false;
};

const extractB2CErrorCode = (errorMessage?: string): string | undefined => {
  const message = sanitizeString(errorMessage);
  if (!message) return undefined;

  const match = message.match(/AADB2C\d{5}/i);
  return match ? match[0].toUpperCase() : undefined;
};

const getRouteLabel = (routeTitle?: string, routePath?: string): string => {
  const title = sanitizeString(routeTitle);
  if (title) return title;

  const path = sanitizeString(routePath);
  if (path) return path;

  return 'Unknown';
};

export function isObservabilityEnabled(environment: ObservabilityEnvironment): boolean {
  const analyticsEnabled = environment.enableAnalytics !== false;
  return Boolean(analyticsEnabled && environment.elasticApm?.enabled);
}

export function initPortalObservability(environment: ObservabilityEnvironment): void {
  const config = environment.elasticApm;
  if (!isObservabilityEnabled(environment)) return;
  if (!config?.serviceName || !config?.serverUrl) return;

  const blockedHosts = normalizeBlockedResourceHosts(environment);

  initApm({
    serviceName: config.serviceName,
    serverUrl: config.serverUrl,
    environment: config.environment || environment.envName,
    serviceVersion: config.serviceVersion || environment.appVersion,
    distributedTracingOrigins: getDistributedTracingOrigins(environment),
    ignoreTransactions: config.ignoreTransactions,
  });

  apm.addFilter((payload: any) => {
    if (!payload) return payload;

    if (Array.isArray(payload.errors)) {
      payload.errors = payload.errors
        .filter((entry: any) => !shouldDropBlockedHostError(entry, blockedHosts))
        .filter(shouldKeepError);
    }

    payload = filterBlockedResourceTransactions(payload, blockedHosts);

    if (Array.isArray(payload.errors) && payload.errors.length === 0 && !payload.transactions) {
      return false;
    }

    return payload;
  });
}

export function registerGlobalErrorHandlers(environment: ObservabilityEnvironment): void {
  if (!isObservabilityEnabled(environment) || typeof window === 'undefined') return;

  const appWindow = window as Window & { [GLOBAL_ERROR_HANDLERS_REGISTRATION_KEY]?: boolean };
  if (appWindow[GLOBAL_ERROR_HANDLERS_REGISTRATION_KEY]) return;
  appWindow[GLOBAL_ERROR_HANDLERS_REGISTRATION_KEY] = true;

  window.addEventListener('error', (event) => {
    const target = event?.target as (EventTarget & { src?: string; href?: string; tagName?: string }) | null;
    const isResourceError = Boolean(target && target !== window && (target.src || target.href));

    if (isResourceError && shouldCaptureAssetLoadFailures(environment)) {
      const assetUrl = sanitizeUrl(target?.src || target?.href || null);
      if (assetUrl) {
        try {
          apm.setCustomContext({
            route: getCurrentRoute(),
            assetUrl: assetUrl,
            assetType: sanitizeString(target?.tagName)?.toLowerCase() || 'resource',
          });
          apm.addLabels({
            failure_type: 'asset_load',
            asset_type: sanitizeString(target?.tagName)?.toLowerCase() || 'resource',
          });
          apm.captureError(new Error('Asset load failure'));
        } catch {
          // Ignore telemetry runtime issues.
        }
      }
      return;
    }

    const runtimeEvent = event as ErrorEvent;
    const runtimeError =
      runtimeEvent.error instanceof Error
        ? runtimeEvent.error
        : new Error(runtimeEvent.message || 'Unhandled runtime error');

    try {
      apm.setCustomContext({
        route: getCurrentRoute(),
        source: sanitizeString(runtimeEvent.filename),
      });
      apm.addLabels({
        failure_type: 'client_error',
        error_type: 'runtime',
      });
      apm.captureError(runtimeError);
    } catch {
      // Ignore telemetry runtime issues.
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const rejectionError =
      reason instanceof Error ? reason : new Error(typeof reason === 'string' ? reason : 'Unhandled promise rejection');

    try {
      apm.setCustomContext({
        route: getCurrentRoute(),
      });
      apm.addLabels({
        failure_type: 'client_error',
        error_type: 'promise_rejection',
      });
      apm.captureError(rejectionError);
    } catch {
      // Ignore telemetry runtime issues.
    }
  });
}

export function isMsalFailureEvent(eventType: EventType): boolean {
  return MSAL_FAILURE_EVENT_TYPES.includes(eventType);
}

export function setApmUserFromAccount(environment: ObservabilityEnvironment, account: AccountInfo | null): void {
  if (!isObservabilityEnabled(environment) || !account) return;

  const claims = (account.idTokenClaims || {}) as IdTokenClaimsWithApmUser;
  const stableUserId = claims.oid || claims.sub;
  if (!stableUserId) return;

  apm.setUserContext({ id: stableUserId });
}

export function captureMsalAuthFailure(environment: ObservabilityEnvironment, message: EventMessage): void {
  if (!isObservabilityEnabled(environment)) return;

  const error = (message.error || {}) as MsalErrorLike;
  const errorMessage = error.errorMessage || error.message || '';
  const b2cCode = extractB2CErrorCode(errorMessage);
  const authErrorCode = sanitizeString(error.errorCode) || b2cCode || 'unknown';

  if (authErrorCode === 'AADB2C90118') {
    return;
  }

  const correlationId = sanitizeString(
    error.correlationId || (message as EventMessage & { correlationId?: string }).correlationId,
  );
  const userCancelled = authErrorCode === 'AADB2C90091' || errorMessage.includes('AADB2C90091');

  try {
    apm.setCustomContext({
      msalEvent: message.eventType,
      errorCode: authErrorCode,
      correlationId: correlationId,
    });
    apm.addLabels({
      failure_type: 'msal_auth',
      auth_stage: String(message.eventType),
      auth_error_code: authErrorCode,
      user_cancelled: String(userCancelled),
    });

    const apmError =
      message.error instanceof Error
        ? message.error
        : new Error(error.errorMessage || error.message || `MSAL ${message.eventType}`);

    apm.captureError(apmError);
  } catch {
    // Ignore telemetry runtime issues.
  }
}

export function captureMsalRedirectFailure(
  environment: ObservabilityEnvironment,
  diagnostics: MsalRedirectFailureDiagnostics = {},
): void {
  if (!isObservabilityEnabled(environment)) return;

  const route = sanitizeString(diagnostics.route) || getCurrentRoute();
  const errorCode = sanitizeString(diagnostics.errorCode) || 'unknown';
  const errorDescription = sanitizeString(diagnostics.errorDescription);
  const rawErrorMessage = sanitizeString(diagnostics.rawErrorMessage);

  try {
    apm.setCustomContext({
      route,
      errorCode,
      errorDescription,
    });
    apm.addLabels({
      failure_type: 'msal_auth',
      auth_stage: 'redirect_handler',
      auth_error_code: errorCode,
    });

    const message = rawErrorMessage || errorDescription || `MSAL redirect failure (${errorCode})`;
    apm.captureError(new Error(message));
  } catch {
    // Ignore telemetry runtime issues.
  }
}

export function captureServerHttpError(
  environment: ObservabilityEnvironment,
  error: HttpErrorResponse,
  diagnostics: ServerHttpErrorDiagnostics = {},
): void {
  if (!isObservabilityEnabled(environment) || error.status < 500) return;
  if (capturedServerHttpErrors.has(error)) return;
  capturedServerHttpErrors.add(error);

  const requestUrl = sanitizeUrl(diagnostics.requestUrl || error.url || null);
  const requestMethod = sanitizeString(diagnostics.requestMethod);
  const endpointPath = getPathname(requestUrl);
  const feature = sanitizeString(diagnostics.feature);
  const correlationId = sanitizeString(diagnostics.correlationId);

  try {
    apm.setCustomContext({
      status: error.status,
      statusText: sanitizeString(diagnostics.statusText || error.statusText),
      url: requestUrl,
      endpoint: endpointPath,
      method: requestMethod,
      feature: feature,
      correlationId: correlationId,
    });
    const labels: { [key: string]: string } = {
      failure_type: 'server_error',
      http_status: String(error.status),
    };
    if (feature) {
      labels.feature = feature;
    }
    apm.addLabels(labels);
    apm.captureError(new Error(error.message || `HTTP ${error.status}`));
  } catch {
    // Ignore telemetry runtime issues.
  }
}

export function captureBrowserNetworkFailure(
  environment: ObservabilityEnvironment,
  error: HttpErrorResponse,
  diagnostics: BrowserNetworkFailureDiagnostics = {},
): void {
  if (!isObservabilityEnabled(environment) || !shouldCaptureBrowserNetworkFailures(environment) || error.status !== 0) {
    return;
  }
  if (capturedBrowserNetworkFailures.has(error)) return;
  capturedBrowserNetworkFailures.add(error);

  const requestUrl = sanitizeUrl(diagnostics.requestUrl || error.url || null);
  const requestMethod = sanitizeString(diagnostics.requestMethod);
  const endpointPath = getPathname(requestUrl);
  const feature = sanitizeString(diagnostics.feature);
  const correlationId = sanitizeString(diagnostics.correlationId);
  const failureReason =
    sanitizeString(diagnostics.failureReason) ||
    (typeof error.error === 'string' ? sanitizeString(error.error) : undefined) ||
    'browser_network_failure';

  try {
    apm.setCustomContext({
      status: 0,
      statusText: sanitizeString(diagnostics.statusText || error.statusText),
      url: requestUrl,
      endpoint: endpointPath,
      method: requestMethod,
      feature: feature,
      correlationId: correlationId,
      route: getCurrentRoute(),
      failureReason: failureReason,
    });
    const labels: { [key: string]: string } = {
      failure_type: 'network_error',
      http_status: '0',
    };
    if (feature) {
      labels.feature = feature;
    }
    apm.addLabels(labels);
    apm.captureError(new Error(error.message || `HTTP 0 ${requestMethod || ''} ${endpointPath || ''}`.trim()));
  } catch {
    // Ignore telemetry runtime issues.
  }
}

export function captureRouteNavigationMetric(
  environment: ObservabilityEnvironment,
  diagnostics: RouteNavigationDiagnostics = {},
): void {
  if (!isObservabilityEnabled(environment)) return;

  const routeLabel = getRouteLabel(diagnostics.routeTitle, diagnostics.routePath || getCurrentRoute());

  try {
    const transaction = apm.startTransaction(`Route - ${routeLabel}`, 'route-change');
    if (!transaction) return;

    transaction.addLabels({
      metric_type: 'route_navigation',
      route: routeLabel,
    });
    transaction.end();
  } catch {
    // Ignore telemetry runtime issues.
  }
}
